import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { resolveClubId } from "../utils/clubScope";
import { fetchApiMe } from "../utils/api/api";
import PlatformSuspendedScreen from "../comp/PlatformSuspendedScreen";

const ClubScopeContext = createContext(null);

export function ClubScopeProvider({ userInfo, children }) {
  const clubID = useMemo(() => resolveClubId(userInfo), [userInfo]);
  const [apiMe, setApiMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [blockedByApi, setBlockedByApi] = useState(false);

  const refreshApiMe = useCallback(async () => {
    // Call even when clubID is not yet resolved — backend binds club from the JWT.
    setMeLoading(true);
    try {
      const data = await fetchApiMe(clubID);
      setApiMe(data);
      setBlockedByApi(false);
      return data;
    } catch (err) {
      const code = err?.error || err;
      if (code === "club_platform_suspended" || err?.response?.data?.error === "club_platform_suspended") {
        setBlockedByApi(true);
      }
      setApiMe(null);
      return null;
    } finally {
      setMeLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    refreshApiMe();
  }, [refreshApiMe]);

  const value = useMemo(
    () => ({ clubID, userInfo, apiMe, refreshApiMe }),
    [clubID, userInfo, apiMe, refreshApiMe]
  );

  const suspended = blockedByApi || apiMe?.platformSuspended === true;

  if (meLoading && !apiMe && !blockedByApi) {
    return (
      <ClubScopeContext.Provider value={value}>
        <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      </ClubScopeContext.Provider>
    );
  }

  if (suspended) {
    return (
      <ClubScopeContext.Provider value={value}>
        <PlatformSuspendedScreen clubID={clubID} />
      </ClubScopeContext.Provider>
    );
  }

  return <ClubScopeContext.Provider value={value}>{children}</ClubScopeContext.Provider>;
}

export function useClubScope() {
  const ctx = useContext(ClubScopeContext);
  return ctx ?? { clubID: null, userInfo: null, apiMe: null, refreshApiMe: async () => null };
}
